import React, { useId, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import { APP_PACKAGE_APIS } from 'redux/slices/appPackage/apis';
import { debounce } from '@tenant/utility/helper/Utils';
import jwtAxios from '@tenant/services/auth/jwt-auth';
import PropTypes from 'prop-types';
import qs from 'qs';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'dockerImageList';
const KEY = 'Id';
const LABEL = 'Name';

const AppPackageSelect = (props) => {
  const {
    error,
    helperText,
    value = [],
    required,
    handleInputChange,
    excludes,
    textFieldProps,
    isMultiple = false,
    queryKey = '',
    params = {},
    ...rest
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const { data, isLoading } = useQuery({
    enabled: isEnabled,
    queryKey: ['app-package-list-select' + queryKey + searchTerm],
    queryFn: () =>
      jwtAxios.get(APP_PACKAGE_APIS.SELECT_BY_APP_IDS, {
        params: { ...params, searchTerm },
        paramsSerializer: (params) => {
          return qs.stringify(params);
        },
      }),
  });

  const id = useId();

  const onInputChange = debounce(async (e, value) => {
    if (e?.type === 'change') {
      setSearchTerm(value);
    }
  }, 500);

  const onOpen = () => {
    setIsEnabled(true);
  };

  return (
    <Autocomplete
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={value}
      id={`${FIELD_NAME}-select-${id}`}
      name={FIELD_NAME}
      options={
        data?.data?.filter((e) => !excludes?.some((f) => f[KEY] === e[KEY])) ??
        []
      }
      getOptionLabel={(option) => option[LABEL]}
      onInputChange={onInputChange}
      onChange={(e, value) => {
        setSearchTerm('');
        rest?.onChange?.(e, value);
      }}
      onOpen={onOpen}
      isOptionEqualToValue={(option, value) => {
        return option?.[KEY] === value?.[KEY];
      }}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props} key={option?.[KEY]}>
            {isMultiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {option[LABEL]}
          </li>
        );
      }}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={
            isMultiple ? 'Select App Packages' : 'Select App Package'
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      {...rest}
    />
  );
};

export default React.memo(AppPackageSelect);

AppPackageSelect.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.array,
  required: PropTypes.bool,
  handleInputChange: PropTypes.func,
  excludes: PropTypes.array,
  textFieldProps: PropTypes.object,
  isMultiple: PropTypes.bool,
  queryKey: PropTypes.string,
  params: PropTypes.object,
};
